class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    // Robust close handlers that survive #CartDrawer.innerHTML rebuilds (which
    // replace the overlay element — dropping its direct listener — and can drop
    // focus out of the drawer, breaking the host-scoped ESC above).
    // - Overlay close via delegation on the host (clicks bubble up from the
    //   replaced overlay).
    // - ESC at the document level so it works regardless of where focus is.
    this.addEventListener('click', (evt) => {
      if (evt.target.closest && evt.target.closest('#CartDrawer-Overlay')) this.close();
    });
    document.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape' && this.classList.contains('active')) this.close();
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, .cart-icon-bubble');
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }


  renderContents(parsedState) {
    // We're rendering items into the drawer, so it's no longer empty. Clear
    // is-empty from BOTH the inner and the host (this theme puts it on the
    // <cart-drawer> host; Dawn only cleared it from .drawer__inner). A stuck
    // is-empty on the host hides the cart contents and the header.
    this.classList.remove('is-empty');
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (sectionElement && newHTML !== null) {
        sectionElement.innerHTML = newHTML;
      }
    });

    setTimeout(() => {
      // Guard the overlay lookup: if it's ever missing, the original code threw
      // here and never reached this.open(), so the cart was updated but the
      // drawer never slid out. Always open regardless.
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector); }

  setActiveElement(element) { this.activeElement = element; }
}

customElements.define('cart-drawer', CartDrawer);

// Catch-all safety net: some add paths in this theme open the cart drawer
// WITHOUT calling CartDrawer.renderContents (e.g. after the cart was emptied,
// the customized updateQuantity wipes #CartDrawer down to the empty-state, and
// the next add opens the drawer without rebuilding it). Whatever opens the
// drawer, if it opens blank (no cart-drawer-items AND no empty-state), rebuild
// #CartDrawer from a fresh section fetch so it never shows an empty drawer.
(function() {
  var healing = false;
  function isBlank(drawer) {
    var inner = drawer.querySelector('.drawer__inner');
    if (!inner) return false;
    return !drawer.querySelector('cart-drawer-items') && !inner.querySelector('.drawer__inner-empty');
  }
  function heal(drawer) {
    if (healing || !isBlank(drawer)) return;
    healing = true;
    fetch('/cart?section_id=cart-drawer')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var src = doc.querySelector('#CartDrawer');
        var live = document.querySelector('#CartDrawer');
        if (src && live) live.innerHTML = src.innerHTML;
        var srcHost = doc.querySelector('cart-drawer');
        if (srcHost) drawer.classList.toggle('is-empty', srcHost.classList.contains('is-empty'));
      })
      .catch(function() {})
      .finally(function() { healing = false; });
  }
  function attach() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(function() {
      if (drawer.classList.contains('active')) heal(drawer);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    if (drawer.classList.contains('active')) heal(drawer);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attach);
  else attach();
})();

// Single-quantity cap — caps the gift product to qty 1 total across ALL its
// variants. Clamps any per-variant qty > 1 inline, and on reconcile drops extra
// variant lines if more than one are present. Driven by window.singleQtyVariantIds
// (rendered from settings.free_gift_product in layout/theme.liquid). Client-side
// safety net only — server-side enforcement still requires a Cart Transform
// Function (covers Shop Pay accelerated and direct API calls).
(function() {
  var CAPPED = (window.singleQtyVariantIds || []).map(String);
  if (!CAPPED.length) return;

  function isCappedRef(ref) {
    if (ref === null || ref === undefined) return false;
    var s = String(ref);
    for (var i = 0; i < CAPPED.length; i++) {
      // Exact variant ID, or line-item key prefix like "12345:abc"
      if (s === CAPPED[i] || s.indexOf(CAPPED[i] + ':') === 0) return true;
    }
    return false;
  }

  function clampJson(obj) {
    var changed = false;
    if (Array.isArray(obj.items)) {
      obj.items.forEach(function(item) {
        if (item && isCappedRef(item.id) && (item.quantity || 0) > 1) {
          item.quantity = 1;
          changed = true;
        }
      });
    }
    if (obj.id !== undefined && isCappedRef(obj.id) && (obj.quantity || 0) > 1) {
      obj.quantity = 1;
      changed = true;
    }
    if (obj.updates && typeof obj.updates === 'object' && !Array.isArray(obj.updates)) {
      Object.keys(obj.updates).forEach(function(k) {
        if (isCappedRef(k) && (obj.updates[k] || 0) > 1) {
          obj.updates[k] = 1;
          changed = true;
        }
      });
    }
    return changed;
  }

  function clampFormLike(fd) {
    var id = fd.get('id');
    var qty = parseInt(fd.get('quantity') || '0', 10);
    if (id && isCappedRef(id) && qty > 1) fd.set('quantity', '1');
    fd.forEach(function(val, key) {
      var m = key.match(/^updates\[([^\]]+)\]$/);
      if (m && isCappedRef(m[1]) && parseInt(val, 10) > 1) {
        fd.set(key, '1');
      }
    });
  }

  function clampInit(init) {
    if (!init || !init.body) return init;
    var body = init.body;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      clampFormLike(body);
      return init;
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      var fd = new FormData();
      body.forEach(function(v, k) { fd.append(k, v); });
      clampFormLike(fd);
      var rebuilt = new URLSearchParams();
      fd.forEach(function(v, k) { rebuilt.append(k, v); });
      init.body = rebuilt;
      return init;
    }
    if (typeof body === 'string') {
      try {
        var json = JSON.parse(body);
        if (clampJson(json)) init.body = JSON.stringify(json);
      } catch (e) { /* not JSON, leave as-is */ }
    }
    return init;
  }

  var origFetch = window.fetch.bind(window);

  function reconcile() {
    return origFetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var items = cart.items || [];
        // Per-variant qty > 1
        var qtyBad = items.filter(function(it) {
          return CAPPED.indexOf(String(it.variant_id)) !== -1 && it.quantity > 1;
        });
        // Multiple distinct variants of the gift product — keep the first, drop the rest
        var giftItems = items.filter(function(it) {
          return CAPPED.indexOf(String(it.variant_id)) !== -1;
        });
        var dupGifts = giftItems.length > 1 ? giftItems.slice(1) : [];

        var ops = qtyBad.map(function(it) { return { id: it.variant_id, qty: 1 }; })
          .concat(dupGifts.map(function(it) { return { id: it.variant_id, qty: 0 }; }));
        if (!ops.length) return false;

        return ops.reduce(function(p, op) {
          return p.then(function() {
            var fd = new FormData();
            fd.append('id', String(op.id));
            fd.append('quantity', String(op.qty));
            return origFetch('/cart/change.js', {
              method: 'POST',
              body: fd,
              credentials: 'same-origin',
              headers: { 'X-Requested-With': 'XMLHttpRequest' }
            }).catch(function() {});
          });
        }, Promise.resolve()).then(function() {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
            try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'single-qty-cap' }); } catch (e) {}
          }
          return true;
        });
      })
      .catch(function() { return false; });
  }

  var reconcileTimer = null;
  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 100);
  }

  // Drops every variant of the gift product from cart. Used to recover when the
  // server-side free-gift threshold validation Function rejects a cart write —
  // typically when removing paid items would leave gift + sub < $200.
  function removeAllGiftLines() {
    return origFetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var updates = {};
        (cart.items || []).forEach(function(it) {
          if (CAPPED.indexOf(String(it.variant_id)) !== -1) updates[it.variant_id] = 0;
        });
        if (!Object.keys(updates).length) return null;
        return origFetch('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ updates: updates }),
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
      });
  }

  window.fetch = function(input, init) {
    var url = '';
    try {
      url = typeof input === 'string' ? input : (input && input.url) || '';
    } catch (e) {}
    var path = url.split('?')[0].replace(/^https?:\/\/[^\/]+/, '');
    var isCartWrite = /\/cart\/(add|change|update)(\.js)?$/.test(path);

    if (isCartWrite) {
      try { init = clampInit(init); } catch (e) {}
    }

    var p = origFetch(input, init);
    if (!isCartWrite) return p;
    p.then(scheduleReconcile, scheduleReconcile);

    // Build retry args. If `input` is a Request its body is consumed by the
    // first fetch; we have to fall back to (input.url, init). For string input
    // the same (input, init) is reusable since cart writes use FormData/string
    // bodies, both of which fetch can read twice.
    var retryInput = (typeof input === 'string') ? input
      : (input && typeof input.url === 'string') ? input.url
      : null;

    // 422 recovery: when the free-gift Function blocks a cart write because the
    // resulting state would be "gift + sub < $200", drop every gift line and
    // replay the original write so the customer's intended edit succeeds.
    // Trigger is cart-state-based (gift present) rather than error-text-based,
    // so it stays robust against changes in Shopify's 422 response shape.
    return p.then(function(response) {
      if (response.status !== 422 || !retryInput) return response;
      return origFetch('/cart.js', { credentials: 'same-origin' })
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(cart) {
          if (!cart) return response;
          var hasGift = (cart.items || []).some(function(it) {
            return CAPPED.indexOf(String(it.variant_id)) !== -1;
          });
          if (!hasGift) return response;
          return removeAllGiftLines().then(function() {
            return origFetch(retryInput, init);
          });
        })
        .catch(function() { return response; });
    });
  };

  // Catch theme-driven cart updates that may have used XHR or other paths
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function(event) {
      if (event && event.source === 'single-qty-cap') return; // avoid loop
      scheduleReconcile();
    });
  }

  // Reconcile any pre-existing exploited cart on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();

// Promo progress bar — lives here so it survives drawer re-renders
(function() {
  var TIERS = [
    { cents: 20000, dollars: 200, reward: 'Free Shipping' },
    { cents: 25000, dollars: 250, reward: '$50 Off' },
    { cents: 50000, dollars: 500, reward: '$100 Off' },
    { cents: 100000, dollars: 1000, reward: '$200 Off' },
    { cents: 200000, dollars: 2000, reward: '$500 Off' }
  ];
  var CHECK = '<path d="M3 7L6 10L11 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';

  window.updatePromoBar = function(totalCents) {
    var bar = document.querySelector('.promo-progress-bar');
    if (!bar) return;

    var totalDollars = Math.floor(totalCents / 100);
    var unlocked = [];
    for (var i = 0; i < TIERS.length; i++) {
      unlocked.push(totalCents >= TIERS[i].cents);
    }
    var allUnlocked = unlocked[4];

    // Message
    var title = bar.querySelector('.promo-progress-bar__title');
    var subtitle = bar.querySelector('.promo-progress-bar__subtitle');
    if (title && subtitle) {
      if (allUnlocked) {
        title.className = 'promo-progress-bar__title promo-progress-bar__title--complete';
        title.textContent = 'Enjoy Free Shipping + $500 Off!';
        subtitle.textContent = 'You unlocked all rewards';
      } else {
        var nextIdx = -1;
        for (var i = 0; i < unlocked.length; i++) {
          if (!unlocked[i]) { nextIdx = i; break; }
        }
        var remaining = TIERS[nextIdx].dollars - totalDollars;
        title.className = 'promo-progress-bar__title';
        title.textContent = 'Add $' + remaining + ' to unlock ' + TIERS[nextIdx].reward;
        subtitle.textContent = 'Next reward at $' + TIERS[nextIdx].dollars;
      }
    }

    // Progress fill — dot centers at 10%, 30%, 50%, 70%, 90%
    var pct = 0;
    if (allUnlocked) {
      pct = 100;
    } else {
      var nextIdx = -1;
      for (var i = 0; i < unlocked.length; i++) {
        if (!unlocked[i]) { nextIdx = i; break; }
      }
      var prevCents = nextIdx === 0 ? 0 : TIERS[nextIdx - 1].cents;
      var nextCents = TIERS[nextIdx].cents;
      var segFrac = (totalCents - prevCents) / (nextCents - prevCents);
      var prevDotPct = nextIdx === 0 ? 0 : (nextIdx - 1) * 20 + 10;
      var nextDotPct = nextIdx * 20 + 10;
      pct = prevDotPct + segFrac * (nextDotPct - prevDotPct);
    }
    pct = Math.min(Math.max(pct, 0), 100);
    var fill = bar.querySelector('.promo-progress-bar__fill');
    if (fill) fill.style.width = pct + '%';

    // Dots
    var dots = bar.querySelectorAll('.promo-progress-bar__dot');
    for (var i = 0; i < dots.length; i++) {
      var svg = dots[i].querySelector('svg');
      if (unlocked[i]) {
        dots[i].classList.add('promo-progress-bar__dot--unlocked');
        if (svg) svg.innerHTML = CHECK;
      } else {
        dots[i].classList.remove('promo-progress-bar__dot--unlocked');
        if (svg) svg.innerHTML = '';
      }
    }
  };

  // Bar swap helpers (global so free gift promo can also use them)
  window.hideShippingBar = function() {
    var sb = document.getElementById('ShippingBarWrapper');
    if (sb) sb.style.display = 'none';
    var pb = document.querySelector('.promo-progress-bar');
    if (pb) pb.style.display = '';
  };

  window.showShippingBar = function() {
    var sb = document.getElementById('ShippingBarWrapper');
    if (sb) sb.style.display = '';
    var pb = document.querySelector('.promo-progress-bar');
    if (pb) pb.style.display = 'none';
  };

  // Check if a matching promo discount code is active (stored in localStorage via ?discount= URL)
  function checkCartDiscount() {
    var codes = window.promoDiscountCodes;
    if (!codes || !codes.length) return;
    var allowed = codes.map(function(c) { return c.trim().toUpperCase(); }).filter(Boolean);
    if (!allowed.length) return;

    var active = (localStorage.getItem('promo_discount_active') || '').toUpperCase();
    if (!active || allowed.indexOf(active) === -1) return;

    // Check TTL
    var activated = parseInt(localStorage.getItem('promo_discount_activated') || '0', 10);
    var ttl = window.promoDiscountTtlMs || (4 * 86400000);
    if (activated && (Date.now() - activated) > ttl) {
      localStorage.removeItem('promo_discount_active');
      localStorage.removeItem('promo_discount_activated');
      return;
    }

    // If discount cookie was removed (e.g. customer removed code at checkout), clear localStorage.
    // The mini-cart can stack codes, so the cookie may hold a comma-joined list — check membership.
    var cookie = document.cookie.split('; ').find(function(c) { return c.startsWith('discount_code='); });
    var cookieCodes = cookie
      ? decodeURIComponent(cookie.split('=')[1]).toUpperCase().split(',').map(function(c) { return c.trim(); }).filter(Boolean)
      : [];
    if (cookieCodes.indexOf(active) === -1) {
      localStorage.removeItem('promo_discount_active');
      localStorage.removeItem('promo_discount_activated');
      return;
    }

    window.hideShippingBar();
    var bar = document.querySelector('.promo-progress-bar');
    if (bar && bar.dataset.cartTotal) {
      window.updatePromoBar(parseInt(bar.dataset.cartTotal, 10));
    }
  }

  // Run on initial load
  function initPromoBar() {
    var bar = document.querySelector('.promo-progress-bar');
    if (bar && bar.dataset.cartTotal) {
      window.updatePromoBar(parseInt(bar.dataset.cartTotal, 10));
    }
  }

  // Expose so the free-gift IIFE below can call it after cleaning up an expired promo
  window.checkCartDiscount = checkCartDiscount;

  // Run on page load
  initPromoBar();
  checkCartDiscount();

  // Re-run whenever drawer content is replaced (MutationObserver on cart-drawer)
  var drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(function() {
      setTimeout(initPromoBar, 10);
    }).observe(drawer, { childList: true, subtree: true });
  }

  // Re-check discount after cart changes
  subscribe(PUB_SUB_EVENTS.cartUpdate, function() {
    checkCartDiscount();
  });

  // Sum rendered post-line-discount totals from each cart row. data-unit-price
  // is pre-discount and would over-count BOGO/automatic discounts.
  function sumRenderedCartCents() {
    var total = 0;
    document.querySelectorAll('cart-drawer-items tr.cart-item').forEach(function(row) {
      var wrapper = row.querySelector('.cart-item__price-wrapper');
      if (!wrapper) return;
      var finalEl = wrapper.querySelector('.cart-item__discounted-prices span.price')
        || wrapper.querySelector('.price--end');
      if (!finalEl) return;
      var dollars = parseFloat(finalEl.textContent.replace(/[^0-9.]/g, ''));
      if (!isNaN(dollars)) total += Math.round(dollars * 100);
    });
    return total;
  }

  // Optimistic update on +/- button clicks
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.quantity__button');
    if (!btn || !btn.closest('cart-drawer-items')) return;
    setTimeout(function() {
      var total = sumRenderedCartCents();
      if (total > 0) window.updatePromoBar(total);
    }, 50);
  });

  // Optimistic update on direct quantity input change
  document.addEventListener('change', function(e) {
    if (!e.target.classList.contains('quantity__input') || !e.target.closest('cart-drawer-items')) return;
    var total = sumRenderedCartCents();
    if (total > 0) window.updatePromoBar(total);
  });
})();

// Free gift promo — manages the exclusive free gift offer for email recipients
(function() {
  var cfg = window.freeGiftPromoConfig;
  if (!cfg) return; // Promo disabled or not configured

  var PRODUCT_HANDLE = cfg.productHandle;
  var LS_KEY = cfg.urlParam + '_eligible';
  var LS_ACTIVATED_KEY = cfg.urlParam + '_activated';
  var CART_TTL_MS = cfg.ttlDays * 24 * 60 * 60 * 1000;
  var CART_THRESHOLD_CENTS = cfg.cartThresholdCents;

  var cachedVariantId = cfg.firstVariantId ? cfg.firstVariantId.toString() : null;
  var cachedProduct = null;
  var isAdding = false;
  var isRemoving = false;
  var updateTimer = null;

  // All variants of the free-gift product. Identity must be by product, not by a
  // single variant — inventory shifts change first_available_variant between page
  // loads, which previously allowed multiple variants to be added and never removed.
  var GIFT_VARIANT_IDS = (window.singleQtyVariantIds || []).map(String);
  if (!GIFT_VARIANT_IDS.length && cachedVariantId) GIFT_VARIANT_IDS = [cachedVariantId];
  function isGiftVariant(id) { return GIFT_VARIANT_IDS.indexOf(String(id)) !== -1; }

  function isEligible() {
    if (!localStorage.getItem(LS_KEY)) return false;
    var activated = parseInt(localStorage.getItem(LS_ACTIVATED_KEY) || '0', 10);
    // No activation timestamp means legacy user — treat as expired so they re-click the link
    if (!activated) return false;
    return (Date.now() - activated) < CART_TTL_MS;
  }

  function getProductData() {
    if (cachedProduct) return Promise.resolve(cachedProduct);
    return fetch('/products/' + PRODUCT_HANDLE + '.js')
      .then(function(r) { return r.json(); })
      .then(function(p) {
        cachedProduct = p;
        if (!cachedVariantId) cachedVariantId = p.variants[0].id.toString();
        return p;
      })
      .catch(function(e) {
        console.error('Free gift promo: could not fetch product', e);
        return null;
      });
  }

  function hidePromo() {
    var el1 = document.getElementById('FreeGiftPromo');
    var el2 = document.getElementById('FreeGiftPromo-Added');
    if (el1) el1.style.display = 'none';
    if (el2) el2.style.display = 'none';
  }


  function refreshDrawer() {
    return fetch('/cart?section_id=cart-drawer')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('#CartDrawer .drawer__inner');
        var target = document.querySelector('#CartDrawer .drawer__inner');
        if (source && target) target.replaceWith(source);
        // The empty/non-empty state lives as .is-empty on the <cart-drawer>
        // host (parent of #CartDrawer), which the inner swap doesn't touch.
        // Sync it so emptying the cart shows the empty-cart view.
        var srcDrawer = doc.querySelector('cart-drawer');
        var liveDrawer = document.querySelector('cart-drawer');
        if (srcDrawer && liveDrawer) {
          liveDrawer.classList.toggle('is-empty', srcDrawer.classList.contains('is-empty'));
        }
      });
  }

  function refreshCartBubble() {
    return fetch('/cart?section_id=cart-icon-bubble')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var section = document.getElementById('cart-icon-bubble');
        if (section) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var content = doc.querySelector('.shopify-section');
          if (content) section.innerHTML = content.innerHTML;
        }
      });
  }

  // Confetti is only used on the gift-add success path. Lazy-load the canvas
  // animation module from assets/cart-confetti.js the first time it's needed
  // so it doesn't ship on every page load. URL is injected via Liquid into
  // window.__azConfettiUrl in layout/theme.liquid.
  var confettiLoadPromise = null;
  function launchConfetti() {
    if (window.__azLaunchConfetti) { window.__azLaunchConfetti(); return; }
    if (!window.__azConfettiUrl) return;
    if (!confettiLoadPromise) {
      confettiLoadPromise = new Promise(function(resolve, reject) {
        var s = document.createElement('script');
        s.src = window.__azConfettiUrl;
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    confettiLoadPromise.then(function() {
      if (window.__azLaunchConfetti) window.__azLaunchConfetti();
    }).catch(function() { /* swallow — confetti is non-critical */ });
  }

  function addGiftToCart() {
    if (isAdding || !cachedVariantId) return;
    isAdding = true;

    var btn = document.getElementById('FreeGiftPromo-Add');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    var fd = new FormData();
    fd.append('id', cachedVariantId);
    fd.append('quantity', 1);

    var config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = fd;

    fetch(window.routes.cart_add_url, config)
    .then(function(r) { return r.json(); })
    .then(function(resp) {
      if (resp.status) throw new Error(resp.description || 'Could not add gift');
      launchConfetti();
      return Promise.all([refreshDrawer(), refreshCartBubble()]);
    })
    .catch(function(e) { console.error('Free gift promo: add error', e); })
    .finally(function() { isAdding = false; });
  }

  function removeGiftFromCart() {
    if (isRemoving || !GIFT_VARIANT_IDS.length) return;
    isRemoving = true;
    hidePromo();

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var giftItems = cart.items.filter(function(it) { return isGiftVariant(it.variant_id); });
        if (!giftItems.length) return null;

        // Optimistically hide each gift row so the drawer doesn't flash them
        giftItems.forEach(function(it) {
          var row = document.querySelector('.cart-item-' + it.variant_id);
          if (row) row.style.display = 'none';
        });

        var updates = {};
        giftItems.forEach(function(it) { updates[it.variant_id] = 0; });
        var config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({ updates: updates });
        return fetch('/cart/update.js', config)
          .then(function() { return Promise.all([refreshDrawer(), refreshCartBubble()]); });
      })
      .catch(function(e) { console.error('Free gift promo: remove error', e); })
      .finally(function() { isRemoving = false; });
  }

  function updatePromo() {
    // Quick exit for visitors without the promo flag
    if (!localStorage.getItem(LS_KEY)) { hidePromo(); return; }

    var eligible = isEligible();

    Promise.all([
      fetch('/cart.js').then(function(r) { return r.json(); }),
      getProductData()
    ]).then(function(results) {
      var cart = results[0];
      var product = results[1];
      if (!product || !cachedVariantId) { hidePromo(); return; }

      var giftInCart = cart.items.some(function(item) {
        return isGiftVariant(item.variant_id);
      });

      // If promo has expired, clean up
      if (!eligible) {
        if (giftInCart && !isRemoving) removeGiftFromCart();
        hidePromo();
        window.showShippingBar();
        if (typeof window.checkCartDiscount === 'function') window.checkCartDiscount();
        return;
      }

      // Post-line-discount: BOGO/automatic line discounts must count, otherwise
      // the banner shows "UNLOCKED" at pre-discount totals (e.g. $225 → $190).
      var totalCents = 0;
      cart.items.forEach(function(item) {
        if (!isGiftVariant(item.variant_id)) {
          totalCents += item.final_line_price;
        }
      });

      var promoEl = document.getElementById('FreeGiftPromo');
      var addedEl = document.getElementById('FreeGiftPromo-Added');
      if (!promoEl || !addedEl) return;

      if (totalCents >= CART_THRESHOLD_CENTS) {
        if (giftInCart) {
          promoEl.style.display = 'none';
          addedEl.style.display = '';
        } else {
          var img = promoEl.querySelector('.free-gift-promo__image');
          if (img && product.images && product.images[0] && img.getAttribute('src') !== product.images[0]) {
            img.src = product.images[0];
            img.alt = product.title;
          }
          var nameEl = promoEl.querySelector('.free-gift-promo__product-name');
          if (nameEl && product.title && nameEl.textContent !== product.title) {
            nameEl.textContent = product.title;
          }
          promoEl.style.display = '';
          addedEl.style.display = 'none';
        }
      } else {
        // Under $200 threshold — remove gift if present
        if (giftInCart && !isRemoving) removeGiftFromCart();
        hidePromo();
      }
    });
  }

  function showPromoBanner() {
    var promoEl = document.getElementById('FreeGiftPromo');
    var addedEl = document.getElementById('FreeGiftPromo-Added');
    if (promoEl && cachedProduct) {
      var img = promoEl.querySelector('.free-gift-promo__image');
      if (img && cachedProduct.images && cachedProduct.images[0] && img.getAttribute('src') !== cachedProduct.images[0]) {
        img.src = cachedProduct.images[0];
        img.alt = cachedProduct.title;
      }
      var nameEl = promoEl.querySelector('.free-gift-promo__product-name');
      if (nameEl && cachedProduct.title && nameEl.textContent !== cachedProduct.title) {
        nameEl.textContent = cachedProduct.title;
      }
      promoEl.style.display = '';
    }
    if (addedEl) addedEl.style.display = 'none';
  }

  // Delegated click handlers (survive drawer re-renders)
  document.addEventListener('click', function(e) {
    if (e.target && (e.target.id === 'FreeGiftPromo-Add' || e.target.closest('#FreeGiftPromo-Add'))) {
      e.preventDefault();
      addGiftToCart();
    }

    // When customer removes the free gift, wait for the row to disappear then show promo
    if (GIFT_VARIANT_IDS.length) {
      var removeBtn = e.target.closest('cart-remove-button');
      if (removeBtn) {
        var row = removeBtn.closest('.cart-item');
        var variantInput = row && row.querySelector('.item_variant_id');
        if (variantInput && isGiftVariant(variantInput.value)) {
          var obs = new MutationObserver(function(mutations, self) {
            var anyGiftRow = GIFT_VARIANT_IDS.some(function(vid) {
              return !!document.querySelector('.cart-item-' + vid);
            });
            if (!anyGiftRow) {
              self.disconnect();
              showPromoBanner();
            }
          });
          obs.observe(document.querySelector('cart-drawer'), { childList: true, subtree: true });
        }
      }
    }
  });

  // Synchronous DOM-based check — no fetch needed, runs instantly on mutations
  // UI-only: never makes cart changes directly (gift removal is handled by the cartUpdate listener)
  function checkPromoFromDOM() {
    if (!localStorage.getItem(LS_KEY) || !isEligible() || !cachedProduct || !GIFT_VARIANT_IDS.length) return;

    // Read final (post-line-discount) price from each rendered row; data-unit-price
    // is pre-discount and would over-count when BOGO/automatic discounts are applied.
    var totalCents = 0;
    document.querySelectorAll('cart-drawer-items tr.cart-item').forEach(function(row) {
      var input = row.querySelector('.quantity__input');
      if (!input || isGiftVariant(input.dataset.quantityVariantId)) return;
      var wrapper = row.querySelector('.cart-item__price-wrapper');
      if (!wrapper) return;
      var finalEl = wrapper.querySelector('.cart-item__discounted-prices span.price')
        || wrapper.querySelector('.price--end');
      if (!finalEl) return;
      var dollars = parseFloat(finalEl.textContent.replace(/[^0-9.]/g, ''));
      if (!isNaN(dollars)) totalCents += Math.round(dollars * 100);
    });

    var giftInCart = GIFT_VARIANT_IDS.some(function(vid) {
      return !!document.querySelector('.cart-item-' + vid);
    });

    var promoEl = document.getElementById('FreeGiftPromo');
    var addedEl = document.getElementById('FreeGiftPromo-Added');
    if (!promoEl || !addedEl) return;

    if (totalCents >= CART_THRESHOLD_CENTS) {
      if (giftInCart) {
        promoEl.style.display = 'none';
        addedEl.style.display = '';
      } else {
        showPromoBanner();
      }
    } else {
      hidePromo();
    }
  }

  // Optimistic UI check on quantity +/- button clicks
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.quantity__button');
    if (!btn || !btn.closest('cart-drawer-items')) return;
    setTimeout(checkPromoFromDOM, 50);
  });

  // Optimistic UI check on direct quantity input changes
  document.addEventListener('change', function(e) {
    if (!e.target.classList.contains('quantity__input') || !e.target.closest('cart-drawer-items')) return;
    checkPromoFromDOM();
  });

  // Initial run (async — pre-fetches product data so it's cached for DOM checks)
  updatePromo();

  // Re-run UI check after drawer content changes
  var drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(function() {
      checkPromoFromDOM();
      clearTimeout(updateTimer);
      updateTimer = setTimeout(updatePromo, 200); // background verification
    }).observe(drawer, { childList: true, subtree: true });
  }

  // Listen for CartItems to finish its update, then handle gift removal safely
  // (avoids racing with the in-flight /cart/change.js from the qty button)
  subscribe(PUB_SUB_EVENTS.cartUpdate, function(event) {
    if (event && event.source === 'cart-items') {
      updatePromo();
    }
  });
})();

// FreeStuds discount-code auto-add — when the "FreeStuds" discount is applied,
// auto-add the studs product it discounts so the customer doesn't have to find &
// add it manually (Vlad's ask). The Shopify "FreeStuds" Buy-X-Get-Y discount is
// what actually makes them free (at checkout, per its own rules); this just puts
// the product in the cart. Config comes from window.freeGiftCodeConfig (rendered
// in layout/theme.liquid); the active flag is set there via ?discount=CODE /
// /discount/CODE / the discount_code cookie.
//
// Targets the REAL studs product the discount applies to — NOT the separate $0
// "(U)" gift product used by the $200-threshold promo — so the two don't overlap
// and no checkout-Function bypass is involved here.
//
// Manual removal is respected for the session (sessionStorage 'freegiftcode_dismissed')
// so the cart doesn't fight the customer; re-clicking the link clears that flag.
(function() {
  var cfg = window.freeGiftCodeConfig;
  if (!cfg || !cfg.code) return;

  var CODE = cfg.code.trim().toUpperCase();
  var TTL_MS = (cfg.ttlDays || 4) * 24 * 60 * 60 * 1000;
  var GIFT_VARIANT_IDS = (cfg.variantIds || []).map(String);
  var ADD_VARIANT_ID = cfg.firstVariantId ? String(cfg.firstVariantId) : (GIFT_VARIANT_IDS[0] || null);
  var ACTIVE_KEY = 'freegiftcode_active';
  var ACTIVATED_KEY = 'freegiftcode_activated';
  var DISMISS_KEY = 'freegiftcode_dismissed';
  // Marks that THIS module added the gift. The same gift product is also given by
  // the $200 free-gift promo, so removal must be scoped to gifts we added — never
  // pull a gift the customer earned the other way.
  var MARK_KEY = 'freegiftcode_added';

  var isAdding = false;
  var isRemoving = false;
  var syncTimer = null;

  function isGiftVariant(id) { return GIFT_VARIANT_IDS.indexOf(String(id)) !== -1; }

  // Active = flag set to our code AND within TTL. Expired flags self-clean.
  function isActive() {
    if ((localStorage.getItem(ACTIVE_KEY) || '').toUpperCase() !== CODE) return false;
    var activated = parseInt(localStorage.getItem(ACTIVATED_KEY) || '0', 10);
    if (!activated || (Date.now() - activated) > TTL_MS) {
      localStorage.removeItem(ACTIVE_KEY);
      localStorage.removeItem(ACTIVATED_KEY);
      return false;
    }
    return true;
  }

  function isDismissed() {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
  }
  function setDismissed() {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) {}
  }

  function refreshDrawer() {
    return fetch('/cart?section_id=cart-drawer')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('#CartDrawer .drawer__inner');
        var target = document.querySelector('#CartDrawer .drawer__inner');
        if (source && target) target.replaceWith(source);
        var srcDrawer = doc.querySelector('cart-drawer');
        var liveDrawer = document.querySelector('cart-drawer');
        if (srcDrawer && liveDrawer) {
          liveDrawer.classList.toggle('is-empty', srcDrawer.classList.contains('is-empty'));
        }
      })
      .catch(function() {});
  }

  function refreshCartBubble() {
    return fetch('/cart?section_id=cart-icon-bubble')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var section = document.getElementById('cart-icon-bubble');
        if (section) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var content = doc.querySelector('.shopify-section');
          if (content) section.innerHTML = content.innerHTML;
        }
      })
      .catch(function() {});
  }

  function refreshCartUI() {
    // On the cart PAGE, our add/remove isn't reflected by the drawer refresh, and
    // re-rendering Dawn's cart-items element piecemeal is fragile — reload so the
    // page shows the change (studs appearing / disappearing). One-shot: after the
    // reload the cart state is settled, so sync() won't add/remove again.
    if (document.getElementById('main-cart-items')) { window.location.reload(); return Promise.resolve(); }
    return Promise.all([refreshDrawer(), refreshCartBubble()]);
  }

  function addGift() {
    if (isAdding || !ADD_VARIANT_ID) return;
    isAdding = true;

    var fd = new FormData();
    fd.append('id', ADD_VARIANT_ID);
    fd.append('quantity', 1);

    var config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = fd;

    fetch(window.routes.cart_add_url, config)
      .then(function(r) { return r.json(); })
      .then(function(resp) {
        if (resp.status) throw new Error(resp.description || 'Could not add FreeStuds product');
        try { localStorage.setItem(MARK_KEY, '1'); } catch (e) {}
        // Attach code FIRST (updates cart.discount_codes), THEN refresh the
        // drawer — so when the discount chip renderer re-reads the cart it
        // finds FreeStuds already there. Otherwise the drawer re-renders
        // with the studs but "Have a discount code?" (empty state) chip.
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(function(r) { return r.json(); })
          .then(function(cart) { return attachCode(cart); })
          .then(function() { return refreshCartUI(); })
          .then(moveGiftToTop)
          .then(function() {
            // Force the mini-cart discount UI to repaint the chip list.
            // Its own MutationObserver on cart-drawer can miss the refresh
            // (bound-to-detached element after cart-drawer re-mounts), and
            // without this the chip section sits at "Have a discount code?"
            // even though cart.discount_codes has FreeStuds.
            if (typeof window.__cartDiscountRefreshList === 'function') {
              window.__cartDiscountRefreshList();
            }
          });
      })
      .catch(function(e) { console.error('FreeStuds auto-add: add error', e); })
      .finally(function() { isAdding = false; });
  }

  function removeGift(cart) {
    if (isRemoving) return;
    var giftItems = (cart.items || []).filter(function(it) { return isGiftVariant(it.variant_id); });
    if (!giftItems.length) return;
    isRemoving = true;

    var updates = {};
    giftItems.forEach(function(it) { updates[it.variant_id] = 0; });
    var config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({ updates: updates });

    fetch(window.routes.cart_update_url || '/cart/update.js', config)
      .then(function(r) { return r.json(); })
      .then(function() { try { localStorage.removeItem(MARK_KEY); } catch (e) {} })
      .then(refreshCartUI)
      .catch(function(e) { console.error('FreeStuds auto-add: remove error', e); })
      .finally(function() { isRemoving = false; });
  }

  // Only do cart work for visitors who actually used the code (active now) or who
  // still have a gift WE added (so we can clean it up). Skipping otherwise avoids a
  // /cart.js fetch on every page for every visitor.
  function weAdded() {
    try { return localStorage.getItem(MARK_KEY) === '1'; } catch (e) { return false; }
  }
  function shouldRun() { return isActive() || weAdded(); }

  // Pin the gift line to the TOP of the cart (above other products and the
  // "complete the look" card), regardless of when it was added. Idempotent — only
  // moves a row that isn't already first, so the reorder observer can't loop.
  function moveGiftToTop() {
    if (!GIFT_VARIANT_IDS.length) return;
    document.querySelectorAll('cart-drawer-items tbody, cart-items tbody, #main-cart-items tbody, .cart-items tbody')
      .forEach(function(tbody) {
        GIFT_VARIANT_IDS.forEach(function(vid) {
          var row = tbody.querySelector('.cart-item-' + vid);
          if (row && row !== tbody.firstElementChild) tbody.insertBefore(row, tbody.firstElementChild);
        });
      });
  }

  // Attach the FreeStuds code to cart.discount_codes so the mini-cart chip
  // renderer shows it. /discount/CODE sets the cookie + applies line-level
  // BXGY, but for BXGY specifically doesn't always add the code to the AJAX
  // cart's discount_codes array. Without this the customer sees "6K+ FreeStuds"
  // as a tag on line items + $0 price, but the "Have a discount code?" section
  // stays empty — which reads as "the discount didn't apply".
  //
  // Merges with existing codes (never wipes them), no-op if already present.
  // Returns a promise. Caller is responsible for refreshing UI afterwards
  // (attachCode intentionally doesn't refresh — that would race the caller's
  // own refreshDrawer and leave the discount chip empty on the first paint).
  function attachCode(cart) {
    var codes = (cart.discount_codes || []).map(function(c) { return c.code; });
    if (codes.map(function(c) { return c.toUpperCase(); }).indexOf(CODE) !== -1) return Promise.resolve();
    codes.push(cfg.code);
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ discount: codes.join(',') })
    }).catch(function() {});
  }

  // Inverse of attachCode. No-op if not present. Also clears the localStorage
  // arm flag so a page load in a fresh session doesn't re-arm the auto-add.
  function detachCode(cart) {
    var codes = (cart.discount_codes || []).map(function(c) { return c.code; });
    if (codes.map(function(c) { return c.toUpperCase(); }).indexOf(CODE) === -1) return Promise.resolve();
    var keep = codes.filter(function(c) { return c.toUpperCase() !== CODE; });
    try {
      localStorage.removeItem('freegiftcode_active');
      localStorage.removeItem('freegiftcode_activated');
    } catch (e) {}
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ discount: keep.join(',') })
    }).catch(function() {});
  }

  // Single source of truth: reconcile cart state with code state.
  function sync() {
    if (!ADD_VARIANT_ID || !shouldRun()) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var items = cart.items || [];
        var giftInCart = items.some(function(it) { return isGiftVariant(it.variant_id); });
        var paidCount = items.filter(function(it) { return !isGiftVariant(it.variant_id); }).length;

        // Gift-with-purchase: the free studs only belong in the cart when there is
        // at least one paid (non-gift) item. No paid item -> no free studs (and
        // pull any we previously added). This is also enforced server-side by the
        // FreeStud Buy-X-Get-Y discount so it can't be gamed.
        var codeOnCart = (cart.discount_codes || []).some(function(c) { return c.code.toUpperCase() === CODE; });

        if (isActive() && !isDismissed() && paidCount > 0) {
          if (!giftInCart) addGift();
          else {
            moveGiftToTop();
            // Studs are here — make sure the code chip shows in the discount UI.
            if (!codeOnCart) {
              attachCode(cart)
                .then(refreshCartUI)
                .then(function() {
                  if (typeof window.__cartDiscountRefreshList === 'function') {
                    window.__cartDiscountRefreshList();
                  }
                });
            }
          }
        } else if (giftInCart && !isRemoving && weAdded()) {
          // No paid item, or code inactive/expired — remove the studs we added.
          // Never touch studs the customer added themselves.
          removeGift(cart);
        } else if (!giftInCart && codeOnCart) {
          // Self-healing: code is on cart but studs aren't. This state is
          // useless (BXGY has no Y to apply against) and reads as "FREESTUDS
          // not applied" in the chip UI. Detach the code so the customer
          // sees a clean empty state, and re-typing the code works fresh.
          detachCode(cart).then(function() {
            if (typeof window.__cartDiscountRefreshList === 'function') {
              window.__cartDiscountRefreshList();
            }
          });
        }
      })
      .catch(function() {});
  }

  function queueSync() {
    if (!shouldRun()) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, 60);
  }

  // When the customer removes the gift themselves, remember it for the session so
  // we don't immediately re-add it (set the flag before the row actually leaves).
  // Also detaches the FreeStuds code from cart.discount_codes so the mini-cart
  // chip section clears alongside the gift (otherwise the chip lingers as
  // "FREESTUDS not applied" with no gift in cart — confusing dead state).
  document.addEventListener('click', function(e) {
    if (!GIFT_VARIANT_IDS.length) return;
    var removeBtn = e.target.closest('cart-remove-button');
    if (!removeBtn) return;
    var row = removeBtn.closest('.cart-item');
    var variantInput = row && row.querySelector('.item_variant_id');
    if (variantInput && isGiftVariant(variantInput.value)) {
      setDismissed();
      // Detach the code + repaint chip list.
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var keep = (cart.discount_codes || [])
            .map(function(c) { return c.code; })
            .filter(function(c) { return c.toUpperCase() !== CODE; });
          return fetch('/cart/update.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ discount: keep.join(',') })
          });
        })
        .then(function() {
          if (typeof window.__cartDiscountRefreshList === 'function') {
            window.__cartDiscountRefreshList();
          }
        })
        .catch(function() {});
    }
  });

  // Keep the gift pinned to the top across drawer/page re-renders. Guarded by
  // shouldRun() so non-FreeStud visitors set up nothing; moveGiftToTop is a no-op
  // when no gift row is present and only reorders a row that isn't already first.
  function observeForReorder() {
    if (!shouldRun()) return;
    [document.querySelector('cart-drawer'), document.getElementById('main-cart-items')]
      .filter(Boolean)
      .forEach(function(t) {
        new MutationObserver(function() { moveGiftToTop(); }).observe(t, { childList: true, subtree: true });
      });
    moveGiftToTop();
  }

  // One-shot cleanup for the stuck "code on cart but no gift" state. Runs on
  // every page load regardless of shouldRun() — otherwise a visitor whose flag
  // was cleared (e.g. TTL expired) but whose cart still has FreeStuds sees the
  // dead "FREESTUDS not applied" chip forever with no way to clear.
  function cleanupStuckCode() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var items = cart.items || [];
        var giftInCart = items.some(function(it) { return isGiftVariant(it.variant_id); });
        var codeOnCart = (cart.discount_codes || []).some(function(c) { return c.code.toUpperCase() === CODE; });
        if (codeOnCart && !giftInCart) {
          return detachCode(cart).then(function() {
            if (typeof window.__cartDiscountRefreshList === 'function') {
              window.__cartDiscountRefreshList();
            }
          });
        }
      })
      .catch(function() {});
  }

  // Initial reconcile + re-run after any cart change.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { sync(); observeForReorder(); cleanupStuckCode(); });
  } else {
    sync();
    observeForReorder();
    cleanupStuckCode();
  }
  subscribe(PUB_SUB_EVENTS.cartUpdate, queueSync);
  // Exposed so the mini-cart discount UI's apply() can force a reconcile after
  // typing the FreeStuds code (which just armed the localStorage flag via
  // window.__freeGiftArm — sync then adds studs + attaches the code + repaints).
  window.__freeGiftSync = sync;
})();

// Cart bundle promo — "Complete the look" card surfaced under any line whose
// product belongs to a bundle that isn't yet complete in the cart. Existing
// line-item-properties on standalone components get rewritten to carry
// _bundle_id / _bundle_size / _bundle_title / _bundle_image; missing components
// get added with the same attrs. The cart-bundle-transformer + bundle-discount
// Shopify Functions then merge the lines and apply 10% off automatically.
(function() {
  var SS_COLLAPSE_KEY = 'cart_bundle_promo_collapsed';
  var SS_DISMISSED_KEY = 'cart_bundle_promo_dismissed';

  function getCollapsedSet() {
    try {
      var raw = sessionStorage.getItem(SS_COLLAPSE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch (e) { return new Set(); }
  }
  function saveCollapsedSet(set) {
    try { sessionStorage.setItem(SS_COLLAPSE_KEY, JSON.stringify(Array.from(set))); } catch (e) {}
  }
  // Dismissed bundles are stored as { handle: [requiredProductId, ...] }. We
  // keep the required ids so a dismissal can be cleared once none of the
  // bundle's products remain in the cart, even when the promo row has been
  // removed from the DOM (product fully removed). The old format was a plain
  // array of handles with no id info; we drop it so stale dismissals don't
  // stick around with no way to clear them.
  function getDismissedMap() {
    try {
      var raw = sessionStorage.getItem(SS_DISMISSED_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return {}; // legacy handle-only format -> discard
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) { return {}; }
  }
  function saveDismissedMap(map) {
    try { sessionStorage.setItem(SS_DISMISSED_KEY, JSON.stringify(map)); } catch (e) {}
  }

  // Walk the rendered drawer to collect product IDs present in cart, including
  // any children of a merged bundle line. Used both for visibility recompute
  // and to decide which components still need to be added on CTA click.
  function getCartProductIds() {
    var ids = [];
    document.querySelectorAll('cart-drawer-items tr.cart-item[data-product-id]').forEach(function(row) {
      if (row.style.display === 'none') return;
      ids.push(String(row.dataset.productId));
    });
    document.querySelectorAll('cart-drawer-items li.cart-item__bundle-component[data-product-id]').forEach(function(li) {
      ids.push(String(li.dataset.productId));
    });
    return ids;
  }

  // True when THIS bundle is actually merged in the cart — i.e. there's a merged
  // bundle row (component list present) tagged with this bundle's _bundle_id.
  // Used for completeness instead of product presence, so a product shared with
  // another bundle can't mark this one complete.
  function isBundleMerged(handle) {
    if (!handle) return false;
    var rows = document.querySelectorAll('cart-drawer-items tr.cart-item[data-bundle-id]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].dataset.bundleId === handle && rows[i].querySelector('li.cart-item__bundle-component')) {
        return true;
      }
    }
    return false;
  }

  // Optimistic: hide cards whose bundle is complete, show ones that aren't.
  // Guard each write against the current value so MutationObserver doesn't loop.
  function recomputeVisibility() {
    var present = getCartProductIds();
    var dismissed = getDismissedMap();
    var dismissedChanged = false;

    // Clear any dismissal whose bundle no longer has a single component in the
    // cart, using the required ids captured at dismiss time. This runs even
    // when the promo row is gone (product fully removed), so re-adding the
    // product shows the promo again instead of staying permanently hidden.
    Object.keys(dismissed).forEach(function(handle) {
      var ids = dismissed[handle] || [];
      if (!ids.length) return;
      var stillPresent = ids.some(function(pid) { return present.indexOf(pid) !== -1; });
      if (!stillPresent) { delete dismissed[handle]; dismissedChanged = true; }
    });

    document.querySelectorAll('.cart-bundle-promo-row').forEach(function(row) {
      var card = row.querySelector('.cart-bundle-promo');
      if (!card) return;
      var required = (card.dataset.requiredProductIds || '').split(',').filter(Boolean);
      var handle = card.dataset.bundleHandle;
      var nextDisplay;
      if (!required.length) {
        nextDisplay = 'none';
      } else {
        var anyPresent = required.some(function(pid) { return present.indexOf(pid) !== -1; });
        if (!anyPresent && dismissed.hasOwnProperty(handle)) {
          delete dismissed[handle];
          dismissedChanged = true;
        }
        if (dismissed.hasOwnProperty(handle)) {
          nextDisplay = 'none';
        } else {
          // Complete (hide) only when THIS bundle is merged. Otherwise show it
          // whenever any of its components is in the cart. Measuring "complete"
          // by merge state (not product presence) keeps a shared product from
          // hiding another bundle's promo.
          nextDisplay = isBundleMerged(handle) ? 'none' : (anyPresent ? '' : 'none');
        }
      }
      if (row.style.display !== nextDisplay) row.style.display = nextDisplay;
    });
    if (dismissedChanged) saveDismissedMap(dismissed);
  }

  // Accordion: at most one promo card is expanded at a time. When several
  // bundle promos are in the cart we keep the first non-collapsed one open and
  // collapse the rest (so the drawer doesn't open fully expanded with many
  // cards). The collapse set is updated so the enforced-collapsed cards persist.
  function restoreCollapseStates() {
    var set = getCollapsedSet();
    var visible = Array.prototype.slice
      .call(document.querySelectorAll('.cart-bundle-promo'))
      .filter(function(card) {
        var row = card.closest('.cart-bundle-promo-row');
        return !row || row.style.display !== 'none';
      });
    // First card the user hasn't explicitly collapsed stays open; everything
    // else collapses. If all are collapsed, none open.
    var keepOpen = null;
    for (var i = 0; i < visible.length; i++) {
      if (!set.has(visible[i].dataset.bundleHandle)) { keepOpen = visible[i]; break; }
    }
    var changed = false;
    visible.forEach(function(card) {
      var details = card.querySelector('.cart-bundle-promo__details');
      if (!details) return;
      var shouldBeOpen = card === keepOpen;
      var handle = card.dataset.bundleHandle;
      if (!shouldBeOpen && handle && !set.has(handle)) { set.add(handle); changed = true; }
      else if (shouldBeOpen && handle && set.has(handle)) { set.delete(handle); changed = true; }
      var isOpen = details.hasAttribute('open');
      if (shouldBeOpen === isOpen) return;
      if (shouldBeOpen) details.setAttribute('open', '');
      else details.removeAttribute('open');
    });
    if (changed) saveCollapsedSet(set);
  }

  // Toggle + persist collapse state via a delegated click on the summary.
  // We take over the toggle explicitly (preventDefault) rather than relying on
  // the native <details> behavior: the summary uses display:flex, which breaks
  // native click-to-toggle on iOS Safari. Manual toggling works everywhere and
  // also lets us persist the state across re-renders.
  document.addEventListener('click', function(e) {
    var summary = e.target.closest && e.target.closest('.cart-bundle-promo__summary');
    if (!summary) return;
    e.preventDefault();
    var details = summary.closest('.cart-bundle-promo__details');
    var card = summary.closest('.cart-bundle-promo');
    if (!details || !card) return;
    var willOpen = !details.hasAttribute('open');
    var set = getCollapsedSet();
    if (willOpen) {
      details.setAttribute('open', '');
      set.delete(card.dataset.bundleHandle);
      // Accordion: collapse every other promo card when this one opens.
      document.querySelectorAll('.cart-bundle-promo').forEach(function(other) {
        if (other === card) return;
        var od = other.querySelector('.cart-bundle-promo__details');
        if (od && od.hasAttribute('open')) od.removeAttribute('open');
        if (other.dataset.bundleHandle) set.add(other.dataset.bundleHandle);
      });
    } else {
      details.removeAttribute('open');
      set.add(card.dataset.bundleHandle);
    }
    saveCollapsedSet(set);
  });

  function refreshDrawer() {
    return fetch('/cart?section_id=cart-drawer')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('#CartDrawer .drawer__inner');
        var target = document.querySelector('#CartDrawer .drawer__inner');
        if (source && target) target.replaceWith(source);
        // The empty/non-empty state lives as .is-empty on the <cart-drawer>
        // host (parent of #CartDrawer), which the inner swap doesn't touch.
        // Sync it so emptying the cart shows the empty-cart view.
        var srcDrawer = doc.querySelector('cart-drawer');
        var liveDrawer = document.querySelector('cart-drawer');
        if (srcDrawer && liveDrawer) {
          liveDrawer.classList.toggle('is-empty', srcDrawer.classList.contains('is-empty'));
        }
      });
  }
  function refreshCartBubble() {
    return fetch('/cart?section_id=cart-icon-bubble')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var section = document.getElementById('cart-icon-bubble');
        if (!section) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('.shopify-section');
        if (content) section.innerHTML = content.innerHTML;
      });
  }

  function setCtaLoading(card, loading) {
    var cta = card.querySelector('.cart-bundle-promo__cta');
    if (!cta) return;
    var spinner = cta.querySelector('.cart-bundle-promo__cta-spinner');
    if (loading) {
      cta.setAttribute('disabled', '');
      if (spinner) spinner.hidden = false;
    } else {
      cta.removeAttribute('disabled');
      if (spinner) spinner.hidden = true;
    }
  }

  function buildBundleProps(card) {
    return {
      _bundle_id: card.dataset.bundleHandle,
      _bundle_size: card.dataset.bundleSize,
      _bundle_title: card.dataset.bundleTitle,
      _bundle_image: card.dataset.bundleImage || ''
    };
  }

  function handleCtaClick(card) {
    var row = card.closest('.cart-bundle-promo-row');
    if (!row) return;
    var ctaEl = card.querySelector('.cart-bundle-promo__cta');
    if (ctaEl && ctaEl.hasAttribute('disabled')) return;

    var components;
    try { components = JSON.parse(card.dataset.components || '[]'); }
    catch (e) { components = []; }
    if (!components.length) return;

    var bundleProps = buildBundleProps(card);
    // Variant-based match: only update / count lines whose variant is one this
    // bundle actually specifies. A different variant of the same product (e.g.
    // rose-gold studs vs. the bundle's platinum studs) stays standalone — it
    // shouldn't be pulled into the bundle and silently get 10% off.
    var bundleVariantSet = {};
    components.forEach(function(c) { bundleVariantSet[String(c.variantId)] = true; });

    setCtaLoading(card, true);
    var originalDisplay = row.style.display;
    row.style.display = 'none';

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var handle = String(card.dataset.bundleHandle || '');
        // A line "belongs to another bundle" when it carries a different
        // _bundle_id. Products shared across bundles must NOT be hijacked: we
        // neither re-tag such lines nor count them as already-present, so this
        // bundle adds its OWN copy of the shared item instead of stealing the
        // other bundle's (which would break that bundle).
        function ownedByOtherBundle(props) {
          var bid = props && props._bundle_id;
          return bid != null && String(bid) !== '' && String(bid) !== handle;
        }
        var existingLines = (cart.items || []).filter(function(it) {
          return bundleVariantSet[String(it.variant_id)] && !ownedByOtherBundle(it.properties);
        });

        // Remember which variants were already in the cart (the products the
        // user added themselves, before completing the look). "Remove bundle"
        // keeps these and only drops the items the look added. Keyed by handle,
        // which equals the _bundle_id we stamp on every line.
        try {
          var origRaw = sessionStorage.getItem('cart_bundle_promo_originals');
          var origMap = origRaw ? JSON.parse(origRaw) : {};
          if (!origMap || typeof origMap !== 'object') origMap = {};
          origMap[card.dataset.bundleHandle] = existingLines.map(function(l) {
            return String(l.variant_id);
          });
          sessionStorage.setItem('cart_bundle_promo_originals', JSON.stringify(origMap));
        } catch (e) {}

        // Update existing lines first so their properties carry the bundle attrs.
        // Properties REPLACE on /cart/change.js, so merge with current line props.
        var chain = existingLines.reduce(function(p, line) {
          return p.then(function() {
            var merged = Object.assign({}, line.properties || {}, bundleProps);
            return fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                id: line.key,
                quantity: line.quantity,
                properties: merged
              })
            });
          });
        }, Promise.resolve());

        // Then add anything missing in a single /cart/add.js call.
        return chain.then(function() {
          // Count variants already in the cart, INCLUDING those nested inside an
          // already-merged bundle line (item_components). Without the nested
          // check we'd think a merged component is missing and re-add it, which
          // produces duplicate component lines in the bundle.
          var presentVariantIds = {};
          (cart.items || []).forEach(function(it) {
            // Lines (or merged children) owned by another bundle don't count as
            // present for this one, so a shared product is re-added with this
            // bundle's id rather than treated as already here.
            if (!ownedByOtherBundle(it.properties)) presentVariantIds[String(it.variant_id)] = true;
            (it.item_components || []).forEach(function(c) {
              if (!ownedByOtherBundle(c.properties)) presentVariantIds[String(c.variant_id)] = true;
            });
          });
          var toAdd = components.filter(function(c) { return !presentVariantIds[String(c.variantId)]; });
          if (!toAdd.length) return;
          var items = toAdd.map(function(c) {
            return { id: c.variantId, quantity: 1, properties: bundleProps };
          });
          return fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items: items })
          });
        });
      })
      .then(function() {
        return Promise.all([refreshDrawer(), refreshCartBubble()]);
      })
      .then(function() {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-bundle-promo' }); } catch (e) {}
        }
      })
      .catch(function(err) {
        console.error('Cart bundle promo: add error', err);
        row.style.display = originalDisplay;
        setCtaLoading(card, false);
      });
  }

  document.addEventListener('click', function(e) {
    var cta = e.target.closest && e.target.closest('.cart-bundle-promo__cta');
    if (!cta) return;
    e.preventDefault();
    var card = cta.closest('.cart-bundle-promo');
    if (!card) return;
    handleCtaClick(card);
  });

  // Ring size picker in the promo card: rewrite the chosen component's variant
  // in data-components so "Get the look" adds the selected size. Capture phase +
  // stopPropagation so this change never reaches CartItems' delegated change
  // handler (which would call updateQuantity with an undefined line index).
  document.addEventListener('change', function(e) {
    var sel = e.target.closest && e.target.closest('.cart-bundle-promo__size[data-ring-size]');
    if (!sel) return;
    e.stopPropagation();
    var card = sel.closest('.cart-bundle-promo');
    if (!card) return;
    var pid = String(sel.dataset.productId);
    var newVariant = parseInt(sel.value, 10);
    if (!newVariant) return;
    try {
      var comps = JSON.parse(card.dataset.components || '[]');
      comps.forEach(function(c) {
        if (String(c.productId) === pid) c.variantId = newVariant;
      });
      card.dataset.components = JSON.stringify(comps);
    } catch (err) {}
  }, true);

  document.addEventListener('click', function(e) {
    var close = e.target.closest && e.target.closest('[data-cart-bundle-promo-close]');
    if (!close) return;
    e.preventDefault();
    e.stopPropagation();
    var card = close.closest('.cart-bundle-promo');
    if (!card) return;
    var handle = card.dataset.bundleHandle;
    if (handle) {
      var dismissed = getDismissedMap();
      dismissed[handle] = (card.dataset.requiredProductIds || '').split(',').filter(Boolean);
      saveDismissedMap(dismissed);
    }
    var row = card.closest('.cart-bundle-promo-row');
    if (row) row.style.display = 'none';
  });

  function scheduleRecompute() {
    setTimeout(function() {
      recomputeVisibility();
      restoreCollapseStates();
    }, 50);
  }

  // Optimistic recompute on the same triggers free-gift uses
  document.addEventListener('click', function(e) {
    if (!e.target.closest) return;
    if (e.target.closest('.quantity__button') && e.target.closest('cart-drawer-items')) {
      scheduleRecompute();
    }
    if (e.target.closest('cart-remove-button') && e.target.closest('cart-drawer-items')) {
      scheduleRecompute();
    }
  });
  document.addEventListener('change', function(e) {
    if (e.target.classList && e.target.classList.contains('quantity__input') && e.target.closest('cart-drawer-items')) {
      scheduleRecompute();
    }
  });

  function init() {
    recomputeVisibility();
    restoreCollapseStates();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(function() {
      recomputeVisibility();
      restoreCollapseStates();
    }).observe(drawer, { childList: true, subtree: true });
  }
})();

// Merged-bundle remove handling. A merged bundle line is virtual (built by the
// Cart Transform from its component lines), so qty controls are hidden on these
// rows (cart-drawer.liquid), leaving only the trash button.
//
// "Remove bundle" keeps the product(s) the user originally added and drops only
// the items the "Get the look" CTA added. The original variants were stashed in
// sessionStorage at CTA time (cart_bundle_promo_originals), keyed by handle.
//
// We must NOT remove children one-by-one: zeroing the first child collapses the
// merge and re-keys the survivors, so the next request hits a stale key (400)
// and leaves items stranded. Instead we drop all the look-added children in a
// single atomic /cart/update.js (evaluated against current state, no re-key
// mid-flight), then re-fetch and strip the bundle properties off the kept
// line(s) so they revert to plain products.
(function() {
  function refreshDrawer() {
    return fetch('/cart?section_id=cart-drawer')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('#CartDrawer .drawer__inner');
        var target = document.querySelector('#CartDrawer .drawer__inner');
        if (source && target) target.replaceWith(source);
        // The empty/non-empty state lives as .is-empty on the <cart-drawer>
        // host (parent of #CartDrawer), which the inner swap doesn't touch.
        // Sync it so emptying the cart shows the empty-cart view.
        var srcDrawer = doc.querySelector('cart-drawer');
        var liveDrawer = document.querySelector('cart-drawer');
        if (srcDrawer && liveDrawer) {
          liveDrawer.classList.toggle('is-empty', srcDrawer.classList.contains('is-empty'));
        }
      });
  }
  function refreshCartBubble() {
    return fetch('/cart?section_id=cart-icon-bubble')
      .then(function(r) { return r.text(); })
      .then(function(html) {
        var section = document.getElementById('cart-icon-bubble');
        if (!section) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('.shopify-section');
        if (content) section.innerHTML = content.innerHTML;
      });
  }

  function setRowLoading(row, loading) {
    if (!row) return;
    // Dim the WHOLE row (not just the price cell, which is all the cell overlay
    // covers) so it's clear the entire line is being removed, and block further
    // clicks while the multi-step removal runs.
    row.classList.toggle('cart-item--removing', !!loading);
    var overlay = row.querySelector('.loading-overlay');
    if (overlay) overlay.classList.toggle('hidden', !loading);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
  }

  function getOriginalsForHandle(handle) {
    try {
      var raw = sessionStorage.getItem('cart_bundle_promo_originals');
      var map = raw ? JSON.parse(raw) : {};
      if (!map || typeof map !== 'object') return [];
      return (map[handle] || []).map(String);
    } catch (e) { return []; }
  }
  function clearOriginalsForHandle(handle) {
    try {
      var raw = sessionStorage.getItem('cart_bundle_promo_originals');
      var map = raw ? JSON.parse(raw) : {};
      if (map && typeof map === 'object' && map.hasOwnProperty(handle)) {
        delete map[handle];
        sessionStorage.setItem('cart_bundle_promo_originals', JSON.stringify(map));
      }
    } catch (e) {}
  }

  function finishRemove(handle, row) {
    clearOriginalsForHandle(handle);
    return Promise.all([refreshDrawer(), refreshCartBubble()])
      .then(function() {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-bundle-remove' }); } catch (e) {}
        }
      });
  }

  // Collect every cart line that belongs to this bundle, whether it's still a
  // merged child (nested in item_components) or has already un-merged into a
  // standalone line. Match on the _bundle_id property so it survives re-keying.
  function findBundleLines(cart, handle) {
    var out = [];
    (cart.items || []).forEach(function(it) {
      var comps = it.item_components || [];
      if (comps.length) {
        // Merged parent line: it's virtual — only its children are real,
        // addressable lines. Never treat the parent as a standalone (editing
        // it un-merges the whole bundle), so collect its children instead.
        comps.forEach(function(c) {
          if (c.properties && c.properties._bundle_id === handle) {
            out.push({ key: c.key, variant_id: c.variant_id, quantity: c.quantity, properties: c.properties, merged: true });
          }
        });
      } else if (it.properties && it.properties._bundle_id === handle) {
        // Genuinely standalone line (un-merged single) — editable directly.
        out.push({ key: it.key, variant_id: it.variant_id, quantity: it.quantity, properties: it.properties, merged: false });
      }
    });
    return out;
  }

  // Remove every line belonging to a bundle, one change.js qty->0 at a time,
  // re-fetching between so keys stay valid as the merge collapses.
  function clearBundleLines(handle, budget) {
    if (budget <= 0) return Promise.resolve();
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var target = findBundleLines(cart, handle)[0];
        if (!target) return;
        return postJSON('/cart/change.js', { id: target.key, quantity: 0 })
          .then(function() { return clearBundleLines(handle, budget - 1); });
      });
  }

  // Change a ring's size on an already-merged bundle. The merged line is virtual
  // and its children aren't reliably addressable, so we rebuild: tear the whole
  // bundle down, then re-add every component (with the ring swapped to the chosen
  // size) carrying _bundle_id/_bundle_size so the Cart Transform re-merges it.
  document.addEventListener('change', function(e) {
    var sel = e.target.closest && e.target.closest('select[data-ring-size][data-cart-swap]');
    if (!sel || !sel.closest('cart-drawer-items')) return;
    // Capture + stop so CartItems' delegated change handler doesn't treat this
    // as a quantity change (it would call updateQuantity with no line index).
    e.stopPropagation();
    var newVariant = parseInt(sel.value, 10);
    var handle = sel.dataset.bundleId;
    if (!newVariant || !handle) return;

    var qty = parseInt(sel.dataset.lineQty, 10) || 1;
    var props = { _bundle_id: handle, _bundle_size: sel.dataset.bundleSize };
    if (sel.dataset.bundleTitle) props._bundle_title = sel.dataset.bundleTitle;
    if (sel.dataset.bundleImage) props._bundle_image = sel.dataset.bundleImage;

    var row = sel.closest('tr.cart-item');
    var ringLi = sel.closest('.cart-item__bundle-component');
    var variants = [];
    var lis = row ? row.querySelectorAll('.cart-item__bundle-component[data-variant-id]') : [];
    Array.prototype.forEach.call(lis, function(li) {
      var vid = (li === ringLi) ? newVariant : parseInt(li.dataset.variantId, 10);
      if (vid) variants.push(vid);
    });
    if (!variants.length) return;

    setRowLoading(row, true);
    clearBundleLines(handle, 20)
      .then(function() {
        return postJSON('/cart/add.js', {
          items: variants.map(function(v) { return { id: v, quantity: qty, properties: props }; })
        });
      })
      .then(function() { return Promise.all([refreshDrawer(), refreshCartBubble()]); })
      .then(function() {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-bundle-ring-size' }); } catch (err) {}
        }
      })
      .catch(function(err) { console.error('Cart bundle ring-size swap failed', err); })
      .finally(function() { if (row) setRowLoading(row, false); });
  }, true);

  function removeBundle(keys, row, handleAttr) {
    setRowLoading(row, true);
    var state = { handle: handleAttr || '', readd: [] };

    // A completed bundle is a SINGLE line in /cart.js — one representative
    // variant carrying { _bundle_id, _bundle_size, _bundle_complete }. The Cart
    // Transform strips our extra props and does NOT expose the components via
    // Ajax, so the cart line tells us nothing about which products formed it.
    // We remove that line (one change.js qty->0 at a time, re-fetching between
    // so keys stay valid) and restore the originals from the list captured at
    // completion time (sessionStorage) — the only record of what to bring back.
    function removeAll(budget) {
      if (budget <= 0) return Promise.resolve(); // safety: never loop forever
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var target = findBundleLines(cart, state.handle)[0];
          if (!target) return;
          return postJSON('/cart/change.js', { id: target.key, quantity: 0 })
            .then(function() { return removeAll(budget - 1); });
        });
    }

    // 1. Read the cart: resolve the handle (if the row didn't carry it) and
    //    decide what to re-add BEFORE we tear the bundle down.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        var lines = findBundleLines(cart, state.handle);
        if (!state.handle) {
          // Derive from the single bundle line's _bundle_id if the row lacked it.
          var any = (cart.items || []).filter(function(it) { return it.properties && it.properties._bundle_id; })[0];
          state.handle = (any && any.properties._bundle_id) || '';
          lines = findBundleLines(cart, state.handle);
        }
        // Originals = the product(s) the user added before completing the look,
        // captured at CTA time. This is the ONLY source for what to restore.
        var originals = getOriginalsForHandle(state.handle);
        if (originals.length) {
          state.readd = originals.map(function(v) {
            return { id: v, quantity: 1, properties: {} };
          });
        } else {
          // Fallback (e.g. completed before this code / in another session):
          // keep the bundle line's representative variant so a product still
          // remains instead of emptying everything out.
          state.readd = lines.map(function(l) {
            return { id: l.variant_id, quantity: 1, properties: {} };
          });
        }
      })
      // 2. Remove the bundle line(s).
      .then(function() { return removeAll(20); })
      // 3. Re-add the kept product(s) as plain items (no _bundle_* props, so the
      //    Cart Transform won't fold them back into a bundle).
      .then(function() {
        if (!state.readd.length) return;
        return postJSON('/cart/add.js', { items: state.readd });
      })
      .then(function() { return finishRemove(state.handle, row); })
      .catch(function(err) { console.error('Cart bundle remove failed', err); })
      .finally(function() { setRowLoading(row, false); });
  }

  document.addEventListener('click', function(e) {
    var btn = e.target.closest && e.target.closest('cart-remove-button');
    if (!btn || !btn.closest('cart-drawer-items')) return;
    var row = btn.closest('tr.cart-item');
    if (!row) return;
    // Dim the whole row immediately for ANY removal (bundle or regular item) so
    // the line clearly reads as "removing" instead of just the price + trash
    // blinking out. Regular items then fall through to Dawn's native remove,
    // which re-renders the drawer and replaces this row.
    row.classList.add('cart-item--removing');

    var raw = row.dataset.bundleComponentKeys || '';
    var keys = raw.split('|').filter(Boolean);
    if (!keys.length) return; // regular item: let Dawn handle the actual removal

    e.stopImmediatePropagation();
    e.preventDefault();
    removeBundle(keys, row, row.dataset.bundleId || '');
  }, true);
})();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Mini-cart discount codes — apply / stack / remove multiple codes via the AJAX
// Cart API's discount param (POST /cart/update.js {discount:'A,B'}). The applied
// codes (each with an `applicable` flag) are read back from cart.discount_codes and
// rendered as a chip list. Codes carry to checkout natively (no cookie needed).
//
// We still mirror the *applicable* codes into the discount_code cookie + promo
// localStorage so the spend-save progress bar keeps lighting up for known promo
// codes. The FreeStuds gift-with-purchase flow is driven by its own marketing link
// and localStorage flags — intentionally untouched here.
(function () {
  var DETAILS_ID = 'Details-CartDrawer-Discount';

  function norm(c) { return (c || '').trim().toUpperCase(); }

  function knownPromo(code) {
    var codes = (window.promoDiscountCodes || []).map(norm);
    return code && codes.indexOf(norm(code)) !== -1;
  }

  // The free-gift code (FreeStuds) is a BXGY discount that Shopify reports as
  // applicable:false until Y is in the cart. Our theme layer auto-adds Y after
  // detecting the code — so the code MUST stick around on the cart in the
  // meantime. Without this guard, the junk cleanup below strips it before the
  // studs are added and Shopify loses it forever for the session.
  function isFreeGiftCode(code) {
    var fg = norm((window.freeGiftCodeConfig || {}).code);
    return fg && norm(code) === fg;
  }
  function isProtected(code) {
    return knownPromo(code) || isFreeGiftCode(code);
  }

  function setMsg(text, kind) {
    var el = document.querySelector('[data-discount-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'cart-drawer__discount-msg' + (kind ? ' is-' + kind : '');
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch];
    });
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  // Apply a code set by POSTing the comma-joined list (the API replaces the whole
  // set, so removal = resend the list minus that code). Returns the updated cart.
  function setCodes(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: codes.join(',') })
    }).then(function (r) { return r.json(); });
  }

  function refreshDrawer() {
    return fetch('/cart?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var src = doc.querySelector('#CartDrawer');
        var live = document.querySelector('#CartDrawer');
        if (src && live) live.innerHTML = src.innerHTML;
      });
  }

  // Keep the promo progress bar working: mirror the codes the bar cares about into
  // the discount_code cookie (which it reads) and flag a known promo code as active.
  // A tiered promo code (july4th / tiereddiscount) is reported applicable:false until
  // the spend threshold is met — but it's still valid and the bar should light up and
  // show progress, so we keep known promo codes regardless of their applicable flag.
  function syncIntegrations(codes) {
    var keep = codes
      .filter(function (c) { return c.applicable || isProtected(c.code); })
      .map(function (c) { return c.code; });
    if (keep.length) {
      document.cookie = 'discount_code=' + encodeURIComponent(keep.join(',')) + '; path=/';
    } else {
      document.cookie = 'discount_code=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    }
    var promo = keep.filter(knownPromo)[0];
    try {
      if (promo) {
        localStorage.setItem('promo_discount_active', norm(promo));
        if (!localStorage.getItem('promo_discount_activated')) {
          localStorage.setItem('promo_discount_activated', Date.now().toString());
        }
      } else {
        localStorage.removeItem('promo_discount_active');
        localStorage.removeItem('promo_discount_activated');
      }
    } catch (e) {}
  }

  // Refresh the spend-save / shipping bar. Must run AFTER refreshDrawer() — the
  // drawer re-render rebuilds those bars, so updating them earlier gets wiped.
  function updateBars() {
    var active = '';
    try { active = localStorage.getItem('promo_discount_active') || ''; } catch (e) {}
    if (active) {
      if (window.checkCartDiscount) window.checkCartDiscount();
    } else if (window.showShippingBar) {
      window.showShippingBar();
    }
  }

  // Render the chip list from cart.discount_codes. Idempotent — keyed on a
  // signature so the MutationObserver below can't self-trigger an endless loop.
  var syncing = false;
  function codeSig(codes) {
    return codes.map(function (c) { return c.code + ':' + (c.applicable ? 1 : 0); }).join('|');
  }
  function render(codes) {
    var details = document.getElementById(DETAILS_ID);
    if (!details) return;
    var list = details.querySelector('[data-discount-list]');
    var sumEl = details.querySelector('[data-discount-summary-text]');
    syncing = true;
    try {
      if (sumEl) {
        var hasApplicable = codes.some(function (c) { return c.applicable; });
        var sumText = hasApplicable ? 'Discount applied' : (codes.length ? 'Code added' : 'Have a discount code?');
        if (sumEl.textContent !== sumText) sumEl.textContent = sumText;
      }
      if (list) {
        var sig = codeSig(codes);
        if (list.dataset.sig !== sig) {
          list.dataset.sig = sig;
          if (!codes.length) {
            list.hidden = true;
            list.innerHTML = '';
          } else {
            list.hidden = false;
            list.innerHTML = codes.map(function (c) {
              var code = escapeHtml(c.code);
              var state, statusText;
              // The Ajax Cart API only returns whether a code applies (boolean) — never
              // WHY it doesn't. Expired, invalid, and "spend tier not met yet" all come
              // back identically as applicable:false. So we must NOT guess a reason here:
              // we previously showed "keep shopping to unlock" for any code in the
              // promo_discount_codes allowlist, which silently lied once a promo expired
              // (the list is hand-maintained and drifts). Show honest state instead.
              // Accurate per-reason messaging (expired vs. tier-not-met) requires the
              // Storefront GraphQL cart.warnings CartWarningCode, which needs a Storefront
              // access token the theme doesn't currently have — see the discount notes.
              if (c.applicable) {
                state = 'is-applied'; statusText = '';
              } else {
                state = 'is-rejected'; statusText = 'not applied';
              }
              var status = statusText ? '<span class="cart-drawer__discount-chip-status">' + statusText + '</span>' : '';
              return '<li class="cart-drawer__discount-chip ' + state + '">' +
                '<span class="cart-drawer__discount-chip-code">' + code + '</span>' + status +
                '<button type="button" class="cart-drawer__discount-chip-remove" data-discount-remove="' + code + '" aria-label="Remove ' + code + '">&times;</button>' +
                '</li>';
            }).join('');
          }
        }
      }
    } finally {
      syncing = false;
    }
  }

  var busy = false;
  function apply(raw) {
    var code = norm(raw);
    if (!code || busy) return;
    busy = true;
    setMsg('Applying…', null);

    // If the typed code is the merchant's free-gift trigger (e.g. FreeStuds),
    // mirror the /discount/FreeStuds behavior: arm the localStorage flag +
    // clear any session dismissal, then let the FreeStud module add the gift
    // + attach the code + repaint. Without this, typing FreeStuds hits the
    // "isn't applied" error path because BXGY needs Y (the studs) in the cart
    // and Y never auto-adds via the typed-code path.
    // If the typed code is the merchant's free-gift trigger (e.g. FreeStuds):
    // arm the localStorage flag + clear session dismissal, then let the
    // FreeStud module add the gift, attach the code, and repaint. Also handles
    // the "already added" retype case (user removed the studs and typed the
    // code again — treat it as a re-arm, don't say "already applied").
    if (isFreeGiftCode(code) && typeof window.__freeGiftArm === 'function') {
      window.__freeGiftArm();
      if (typeof window.__freeGiftSync === 'function') window.__freeGiftSync();
      setMsg('Discount applied.', 'ok');
      var input = document.querySelector('[data-discount-input]');
      if (input) input.value = '';
      busy = false;
      return;
    }

    getCart().then(function (cart) {
      var existing = (cart.discount_codes || []).map(function (c) { return c.code; });
      if (existing.map(norm).indexOf(code) !== -1) {
        setMsg('That code is already added.', null);
        busy = false;
        return;
      }
      return setCodes(existing.concat([code])).then(function (cart2) {
        var all = cart2.discount_codes || [];
        var entry = all.filter(function (c) { return norm(c.code) === code; })[0];
        var input = document.querySelector('[data-discount-input]');
        var applied = entry && entry.applicable;
        if (applied) {
          setMsg('Discount applied.', 'ok');
          if (input) input.value = '';
        } else {
          // The code didn't apply — tell the buyer, but don't leave it lingering in
          // the cart. Shopify keeps non-applicable codes forever, so we resend only
          // the applicable ones (dropping this code plus any junk already there).
          setMsg(entry
            ? '“' + entry.code + '” isn’t applied to your current cart — it may need a qualifying item, or can’t combine with your other codes.'
            : 'That code isn’t valid.', 'error');
        }
        var keep = all.filter(function (c) { return c.applicable || isProtected(c.code); }).map(function (c) { return c.code; });
        var settle = keep.length === all.length ? Promise.resolve(all) : setCodes(keep).then(function (c3) { return c3.discount_codes || []; });
        return settle.then(function (codes) {
          syncIntegrations(codes);
          return refreshDrawer().then(function () {
            render(codes);
            updateBars();
            var details = document.getElementById(DETAILS_ID);
            if (details) details.open = true;
          });
        });
      });
    }).catch(function () {
      setMsg('Something went wrong. Please try again.', 'error');
    }).finally(function () { busy = false; });
  }

  function remove(rawCode) {
    if (busy) return;
    var code = norm(rawCode);
    busy = true;
    setMsg('', null);
    getCart().then(function (cart) {
      var remaining = (cart.discount_codes || [])
        .map(function (c) { return c.code; })
        .filter(function (c) { return norm(c) !== code; });
      return setCodes(remaining).then(function (cart2) {
        var codes = cart2.discount_codes || [];
        syncIntegrations(codes);
        return refreshDrawer().then(function () { render(codes); updateBars(); });
      });
    }).catch(function () {
      setMsg('Something went wrong. Please try again.', 'error');
    }).finally(function () { busy = false; });
  }

  // Delegated handlers — survive drawer re-renders (innerHTML gets replaced).
  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-discount-remove]');
    if (rm) { remove(rm.getAttribute('data-discount-remove')); return; }
    if (e.target.closest('[data-discount-apply]')) {
      var input = document.querySelector('[data-discount-input]');
      apply(input ? input.value : '');
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) {
      e.preventDefault();
      apply(e.target.value);
    }
  });

  // Reflect applied codes on load and whenever the drawer re-renders (a Dawn cart
  // update replaces #CartDrawer, wiping our injected chips, so repopulate from the
  // server). Debounced; the signature guard in render() ends the observe→write loop.
  var obsTimer;
  function refreshList() {
    getCart().then(function (cart) {
      var all = cart.discount_codes || [];
      // Don't fight an in-flight apply/remove; it settles the list itself.
      if (busy) { render(all); return; }
      var junk = all.filter(function (c) { return !c.applicable && !isProtected(c.code); });
      if (!junk.length) { render(all); return; }
      // Drop stale/expired/invalid codes Shopify has been retaining so they don't
      // pile up in the drawer with no way to clear them. Protected codes
      // (free-gift trigger + promo bar codes) always stay.
      var keep = all.filter(function (c) { return c.applicable || isProtected(c.code); }).map(function (c) { return c.code; });
      setCodes(keep).then(function (cart2) {
        var codes = cart2.discount_codes || [];
        syncIntegrations(codes);
        render(codes);
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshList);
  else refreshList();
  // Exposed so other modules (FreeStud auto-add) can force a repaint of the
  // discount chip list after they mutate cart.discount_codes — the
  // MutationObserver below can miss those mutations if it was bound to a
  // now-detached cart-drawer element.
  window.__cartDiscountRefreshList = refreshList;
  // Exposed so the mini-cart apply() can arm the FreeStud auto-add flow when
  // the user types "FreeStuds" in the discount field (instead of using the
  // /discount/FreeStuds link). Without this, typing shows "isn't applied"
  // because BXGY requires Y in cart, but the studs never auto-add.
  window.__freeGiftArm = function () {
    var cfg = window.freeGiftCodeConfig;
    if (!cfg || !cfg.code) return false;
    try {
      localStorage.setItem('freegiftcode_active', cfg.code.trim().toUpperCase());
      localStorage.setItem('freegiftcode_activated', String(Date.now()));
      sessionStorage.removeItem('freegiftcode_dismissed');
    } catch (e) {}
    return true;
  };
  var drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(function () {
      if (syncing) return;
      clearTimeout(obsTimer);
      obsTimer = setTimeout(refreshList, 120);
    }).observe(drawer, { childList: true, subtree: true });
  }
})();

